import React from 'react';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { FiBlobAvatar } from './fi-blob-avatar';
import { FI_ABOUT_EXPECTATIONS, FI_ABOUT_INTRO, FI_SCOPE_LINE } from './fi-about-copy';
import { FI_ASSISTANT_BUBBLE_RADII, FI_BUBBLE_ASSISTANT_BG, FI_BUBBLE_ASSISTANT_TEXT } from './fi-styles';
import { FI_OPENING_MESSAGE } from './fi-starter-actions';

interface Props {
  onBack: () => void;
  onClose: () => void;
}

/**
 * About drawer — full-panel takeover explaining how Fi works as a concierge.
 */
export const FiAboutDrawer: React.FC<Props> = ({ onBack, onClose }) => {
  const isMdUp = useMediaQuery('md');

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div
        className={
          isMdUp
            ? 'bg-white px-2.5 pt-3'
            : 'sticky top-0 z-[2] bg-white px-2.5 pt-[calc(env(safe-area-inset-top,0px)+12px)]'
        }
      >
        <header className="shrink-0 bg-white">
          <div className="flex min-h-[64px] items-center gap-1 px-1 py-1.5">
            <button
              type="button"
              aria-label="Back to conversation"
              onClick={onBack}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-700 hover:bg-gray-100"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="font-albert-sans min-w-0 flex-1 truncate text-center text-sm font-semibold text-gray-900">
              About Fi
            </p>
            <button
              type="button"
              aria-label="Close Fi chat"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0 text-gray-600 hover:bg-gray-100"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="bg-input-border-gray h-px w-full" aria-hidden />
        </header>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto px-5 py-5"
        style={{ paddingBottom: 'max(20px, calc(20px + env(safe-area-inset-bottom, 0px)))' }}
      >
        <div className="flex flex-col items-center text-center">
          <FiBlobAvatar size={72} />
          <p className="font-albert-sans mt-4 text-2xl font-semibold text-gray-900">Fi</p>
          <p className="font-open-sans mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-orange-600">
            Concierge
          </p>
          {FI_SCOPE_LINE ? (
            <p className="font-open-sans mt-3 max-w-[26rem] text-sm leading-snug text-gray-600">{FI_SCOPE_LINE}</p>
          ) : null}
        </div>

        <div className="bg-input-border-gray my-5 h-px w-full" aria-hidden />

        <section className="rounded-2xl bg-orange-50/50 p-4">
          <h3 className="font-albert-sans text-sm font-semibold text-gray-900">How Fi helps</h3>
          <p className="font-open-sans mt-2 text-sm leading-6 text-gray-800">{FI_ABOUT_INTRO}</p>
          <div className="mt-3 flex items-start gap-2">
            <FiBlobAvatar size={32} />
            <div
              className={`min-w-0 ${FI_ASSISTANT_BUBBLE_RADII} px-5 py-3.5`}
              style={{ backgroundColor: FI_BUBBLE_ASSISTANT_BG }}
            >
              <p
                className="font-open-sans whitespace-pre-wrap break-words text-sm leading-5"
                style={{ color: FI_BUBBLE_ASSISTANT_TEXT }}
              >
                {FI_OPENING_MESSAGE}
              </p>
            </div>
          </div>
        </section>

        <section className="mt-4 rounded-2xl bg-orange-50/50 p-4">
          <h3 className="font-albert-sans text-sm font-semibold text-gray-900">What to expect</h3>
          <ul className="font-open-sans mt-2 space-y-2 text-sm leading-6 text-gray-800">
            {FI_ABOUT_EXPECTATIONS.map((line) => (
              <li key={line} className="flex gap-2">
                <span aria-hidden className="text-orange-500">
                  •
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </section>

        <div className="mt-6">
          <button
            type="button"
            onClick={onBack}
            className="font-albert-sans w-full rounded-full bg-gray-900 px-5 py-3 text-sm font-semibold text-white hover:bg-gray-800"
          >
            Back to the conversation
          </button>
        </div>
      </div>
    </div>
  );
};
